import { FunctionComponent, memo, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Markdown } from "@edgetier/components";
import { InteractionMessageType, InteractionType, MessageDirection } from "@edgetier/watchtower-types";
import { NavLink } from "react-router-dom";
import Page from "constants/page";
import TagList from "components-for/interactions/interaction-content/interaction-tags/tag-list";
import classnames from "classnames";
import { IProps } from "./interaction-message-content.types";
import SentimentIcons from "components-for/sentiment/sentiment-icons";
import { InteractionContext } from "components-for/interactions/interaction-context/interaction-context";
import { formatWritingIssueMessage } from "utilities-for/writing-issues/format-writing-issue-message";
import Qs from "qs";
import { useInView } from "react-intersection-observer";
import { MESSAGE_IN_VIEW_THRESHOLD } from "./interaction-message-content.constants";
import { LoadingSkeleton } from "@edgetier/client-components";
import { getSkeletonSize } from "./interaction-message-content.utilities";
import "./interaction-message-content.scss";

/**
 * The content of an interaction message.
 * @param props.ignoreDirection Should a message be styled to show if it's incoming or outgoing.
 * @param props.message         The message object to display.
 * @param props.writingIssues   An array of writing issues associated with the message being displayed.
 * @returns The text of an interaction message and the time that the message was sent.
 */
const InteractionMessageContent: FunctionComponent<IProps> = ({
    interactionType,
    ignoreDirection = false,
    message,
    writingIssues,
    senderDetails,
    activeTranslation,
    toggleTranslation,
}) => {
    const { areWritingIssuesVisible, hasViewAgentPermission, isTranslationActive } = useContext(InteractionContext);

    const { ref: messageRef, inView: isMessageInView } = useInView({
        threshold: MESSAGE_IN_VIEW_THRESHOLD,
    });

    // While loading, try to get the number of lines and width of the message so that it doesn't jump around too much.
    const [skeletonSize, setSkeletonSize] = useState<{ lineCount: number; width: string | number }>({
        lineCount: 1,
        width: "100%",
    });

    const textRef = useRef<HTMLDivElement>(null);

    const messageText = useMemo(() => {
        const messageContent =
            typeof activeTranslation === "undefined"
                ? message.message
                : isTranslationActive &&
                  typeof activeTranslation !== "undefined" &&
                  activeTranslation.isTranslated &&
                  activeTranslation.translatedText.trim().length > 0
                ? activeTranslation.translatedText
                : message.message;

        // If writing issues should be visible, highlight the spelling and grammar issues.
        if (areWritingIssuesVisible) {
            return formatWritingIssueMessage(writingIssues, messageContent);
        }
        return messageContent;
    }, [activeTranslation, areWritingIssuesVisible, isTranslationActive, message.message, writingIssues]);

    useEffect(() => {
        if (isTranslationActive && message.messageType !== InteractionMessageType.System) {
            if (isMessageInView && typeof activeTranslation === "undefined") {
                toggleTranslation(message.interactionMessageId, message.message, message.languageIsoCode);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        activeTranslation,
        isMessageInView,
        isTranslationActive,
        message.interactionMessageId,
        message.languageIsoCode,
        message.message,
        message.messageType,
    ]);

    // Count the number of lines so that the loading skeleton is roughly the same size while loading.
    useLayoutEffect(() => {
        if (textRef.current) {
            setSkeletonSize(getSkeletonSize(textRef.current));
        }
    }, [textRef]);

    return (
        <div
            className={classnames("interaction-message-content", {
                "interaction-message-content--in": !ignoreDirection && message.direction === MessageDirection.Inbound,
                "interaction-message-content--out": !ignoreDirection && message.direction === MessageDirection.Outbound,
            })}
            ref={messageRef}
        >
            <div className="interaction-message-content__text" ref={textRef}>
                {typeof activeTranslation !== "undefined" ? (
                    <>
                        {activeTranslation.isLoading ? (
                            <LoadingSkeleton count={skeletonSize.lineCount} style={{ width: skeletonSize.width }} />
                        ) : (
                            <Markdown markdownString={messageText} />
                        )}
                    </>
                ) : (
                    <Markdown markdownString={messageText} />
                )}
            </div>
            <div className="interaction-message-content__footer">
                {Array.isArray(message.tags) && message.tags.length > 0 && <TagList tags={message.tags} />}
                <div className="interaction-message-content__extra-information">
                    <SentimentIcons
                        hasFrustration={message.hasFrustration}
                        hasGratitude={message.hasGratitude}
                        hasPraise={message.hasPraise}
                    />
                    <div className="interaction-message-content__sender-details">
                        {typeof senderDetails === "string" && message.messageType !== InteractionMessageType.System && (
                            <>
                                {typeof message.agentId === "string" &&
                                message.direction === MessageDirection.Outbound &&
                                hasViewAgentPermission ? (
                                    <NavLink
                                        className={classnames({
                                            "interaction-message-content__agent-name":
                                                message.direction === MessageDirection.Outbound,
                                        })}
                                        to={{
                                            pathname: Page.Agent,
                                            search: Qs.stringify(
                                                { reviewAgentId: message.agentId },
                                                { arrayFormat: "repeat", skipNulls: true }
                                            ),
                                        }}
                                    >
                                        {senderDetails}
                                    </NavLink>
                                ) : (
                                    <div className="interaction-message-content__name">{senderDetails}</div>
                                )}
                            </>
                        )}

                        <div className="interaction-message-content__time">
                            {interactionType === InteractionType.Call ? "said on" : "sent on"}
                            <span>{format(message.sendDateTime, "dd MMM yyyy 'at' HH:mm:ss")}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default memo(InteractionMessageContent);
